import {
  CHECKLIST_ACTIVE_STATUSES,
  CHECKLIST_MARKETING_TYPE,
  type ChecklistOverview,
  type EstateRow,
} from '@acasa/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'

/** Estate attribute columns the user can show or hide. ImmoNr is always on. */
export const ATTRIBUTE_COLUMNS = [
  { key: 'title', label: 'Objekt' },
  { key: 'type', label: 'Art' },
  { key: 'marketingType', label: 'Vermarktung' },
  { key: 'postalCode', label: 'PLZ' },
  { key: 'city', label: 'Ort' },
  { key: 'status', label: 'Status' },
  { key: 'activeState', label: 'Aktiv/Inaktiv' },
  { key: 'agent', label: 'Betreuer' },
  { key: 'createdAt', label: 'Angelegt' },
  { key: 'modifiedAt', label: 'Geändert' },
  { key: 'soldAt', label: 'Verkauft am' },
  { key: 'progress', label: 'Vollständigkeit' },
  { key: 'open', label: 'Offen' },
] as const

export type AttributeKey = (typeof ATTRIBUTE_COLUMNS)[number]['key']

export type Completeness = 'all' | 'incomplete' | 'complete'

/** Value used for estates onOffice left without a status2. */
export const NO_STATUS = '(ohne Status)'

/**
 * Phases where chasing documents is worthwhile — the out-of-the-box scope.
 *
 * Re-exported from `@acasa/shared` rather than declared here: the Übersicht
 * counts have to be the same objects this table lists, and two copies of the
 * scope is how a dashboard ends up quietly contradicting the page behind it.
 */
export const ACTIVE_STATUSES: string[] = [...CHECKLIST_ACTIVE_STATUSES]
export type SortDirection = 'asc' | 'desc'

/** `attr:<key>` for an estate column, `doc:<key>` for a matrix column. */
export type SortKey = string

export type TableState = {
  search: string
  completeness: Completeness
  /**
   * Marketing phase. SCOPE rather than an ad-hoc filter — it decides which
   * objects the tool is about, so it persists and survives "Zurücksetzen".
   */
  status: string[]
  /** `status` — Aktiv / Inaktiv. Scope, like the marketing phase. */
  activeState: string[]
  agent: string[]
  type: string[]
  city: string[]
  /**
   * Kauf / Miete / Erbpacht. Treated as SCOPE rather than an ad-hoc filter (so
   * it persists): the 23 documents are a purchase due-diligence list, and no
   * rental object in this account has ever had a single box ticked.
   */
  marketingType: string[]
  /** Only estates missing ALL of these documents. */
  missingDocuments: string[]
  visibleAttributes: AttributeKey[]
  showMatrix: boolean
  /** Empty = every document column. */
  visibleDocuments: string[]
  sortKey: SortKey
  sortDirection: SortDirection
  compact: boolean
}

export const DEFAULT_STATE: TableState = {
  search: '',
  completeness: 'incomplete',
  status: ACTIVE_STATUSES,
  activeState: [],
  agent: [],
  type: [],
  city: [],
  marketingType: [CHECKLIST_MARKETING_TYPE],
  missingDocuments: [],
  visibleAttributes: ['title', 'marketingType', 'city', 'agent', 'createdAt', 'progress', 'open'],
  /*
   * OFF for a fresh browser. The 23 document columns are the page's reason to
   * exist, but as a first impression they are a wall of marks nobody asked for;
   * the attribute columns alone read as a list. Somebody who switches the
   * matrix on keeps it — `showMatrix` is persisted, so this default only ever
   * applies where nothing has been stored yet.
   */
  showMatrix: false,
  visibleDocuments: [],
  sortKey: 'attr:open',
  sortDirection: 'desc',
  compact: true,
}

const STORAGE_KEY = 'acasa.checklists.table.v5'

/**
 * What survives a reload. Layout and ordering are remembered; the ad-hoc
 * filters (search, facets, missing-document) deliberately reset, so opening the
 * page never hides rows for a reason you have forgotten about.
 */
const PERSISTED_KEYS = [
  'visibleAttributes',
  'showMatrix',
  'visibleDocuments',
  'compact',
  'completeness',
  'marketingType',
  'status',
  'activeState',
  'sortKey',
  'sortDirection',
] as const

const KNOWN_ATTRIBUTES = new Set<string>(ATTRIBUTE_COLUMNS.map((column) => column.key))

/**
 * Restores saved preferences, discarding anything that no longer makes sense —
 * a column removed from the code must not linger in someone's localStorage and
 * render a blank header.
 */
function loadPersisted(): Partial<TableState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}

    const parsed = JSON.parse(raw) as Partial<TableState>
    const restored: Partial<TableState> = Object.fromEntries(
      PERSISTED_KEYS.filter((key) => key in parsed).map((key) => [key, parsed[key]]),
    )

    if (Array.isArray(restored.visibleAttributes)) {
      const valid = restored.visibleAttributes.filter((key) => KNOWN_ATTRIBUTES.has(key))
      // An empty column set would render an unusable table — fall back.
      restored.visibleAttributes = valid.length > 0 ? valid : DEFAULT_STATE.visibleAttributes
    }

    if (
      restored.completeness &&
      !['all', 'incomplete', 'complete'].includes(restored.completeness)
    ) {
      restored.completeness = DEFAULT_STATE.completeness
    }

    if (restored.sortDirection !== 'asc' && restored.sortDirection !== 'desc') {
      delete restored.sortDirection
    }

    return restored
  } catch {
    // A corrupt or unavailable localStorage must never break the page.
    return {}
  }
}

export function useTableState() {
  const [state, setState] = useState<TableState>(() => ({ ...DEFAULT_STATE, ...loadPersisted() }))

  useEffect(() => {
    try {
      const toPersist = Object.fromEntries(PERSISTED_KEYS.map((key) => [key, state[key]]))
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist))
    } catch {
      // Private browsing / quota — layout preferences are not worth an error.
    }
  }, [state])

  const patch = useCallback(
    (changes: Partial<TableState>) => setState((prev) => ({ ...prev, ...changes })),
    [],
  )

  const toggleSort = useCallback(
    (key: SortKey) =>
      setState((prev) =>
        prev.sortKey === key
          ? { ...prev, sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc' }
          : // Documents and counts are most useful "worst first"; text ascending.
            {
              ...prev,
              sortKey: key,
              sortDirection: key.startsWith('attr:') && !NUMERIC.has(key) ? 'asc' : 'desc',
            },
      ),
    [],
  )

  const resetFilters = useCallback(
    () =>
      patch({
        // Scope (completeness / status / marketingType) is deliberately kept:
        // resetting filters should not silently change what the tool is about.
        search: '',
        agent: [],
        type: [],
        city: [],
        missingDocuments: [],
      }),
    [patch],
  )

  return { state, patch, toggleSort, resetFilters }
}

const NUMERIC = new Set([
  'attr:open',
  'attr:progress',
  'attr:createdAt',
  'attr:modifiedAt',
  'attr:soldAt',
])

const STATE_ORDER = { open: 0, not_applicable: 1, done: 2 } as const

function attributeValue(estate: EstateRow, key: string): string | number {
  switch (key) {
    case 'immoNr':
      return estate.externalNumber ?? String(estate.estateId)
    case 'title':
      return estate.title ?? ''
    case 'type':
      return estate.type ?? ''
    case 'marketingType':
      return estate.marketingType ?? ''
    case 'postalCode':
      return estate.postalCode ?? ''
    case 'city':
      return estate.city ?? ''
    case 'status':
      return estate.status ?? ''
    case 'activeState':
      return estate.activeState ?? ''
    case 'agent':
      return estate.agent ?? ''
    case 'createdAt':
      // Nulls sort as "unknown, oldest" rather than jumping to the top.
      return estate.createdAt ? Date.parse(estate.createdAt) : 0
    case 'modifiedAt':
      return estate.modifiedAt ? Date.parse(estate.modifiedAt) : 0
    case 'soldAt':
      return estate.soldAt ? Date.parse(estate.soldAt) : 0
    case 'open':
      return estate.openCount
    case 'progress':
      return estate.totalCount === 0
        ? 0
        : (estate.doneCount + estate.notApplicableCount) / estate.totalCount
    default:
      return ''
  }
}

/**
 * Applies search, facet filters and sorting in the browser.
 *
 * Kept as a pure function of (data, state) so the table never holds derived
 * state of its own — every interaction is a single re-render from source.
 */
export function useVisibleEstates(data: ChecklistOverview | undefined, state: TableState) {
  return useMemo(() => {
    if (!data) return []

    const needle = state.search.trim().toLowerCase()

    const filtered = data.estates.filter((estate) => {
      if (state.completeness === 'incomplete' && estate.openCount === 0) return false
      if (state.completeness === 'complete' && estate.openCount > 0) return false

      if (state.status.length > 0 && !state.status.includes(estate.status ?? NO_STATUS)) {
        return false
      }
      if (state.agent.length > 0 && !state.agent.includes(estate.agent ?? '')) return false
      if (state.type.length > 0 && !state.type.includes(estate.type ?? '')) return false
      if (state.city.length > 0 && !state.city.includes(estate.city ?? '')) return false
      if (state.activeState.length > 0 && !state.activeState.includes(estate.activeState ?? '')) {
        return false
      }
      if (
        state.marketingType.length > 0 &&
        !state.marketingType.includes(estate.marketingType ?? '')
      ) {
        return false
      }

      // "Missing" means genuinely outstanding — a document marked not-applicable
      // is resolved and must not match.
      if (state.missingDocuments.some((key) => estate.steps[key]?.state !== 'open')) return false

      if (needle) {
        const haystack = [
          estate.externalNumber,
          estate.title,
          estate.city,
          estate.postalCode,
          estate.agent,
          estate.type,
          estate.marketingType,
          estate.status,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(needle)) return false
      }

      return true
    })

    const direction = state.sortDirection === 'asc' ? 1 : -1

    return [...filtered].sort((a, b) => {
      let result = 0

      if (state.sortKey.startsWith('doc:')) {
        const key = state.sortKey.slice(4)
        const left = STATE_ORDER[a.steps[key]?.state ?? 'open']
        const right = STATE_ORDER[b.steps[key]?.state ?? 'open']
        result = left - right
      } else {
        const key = state.sortKey.slice(5)
        const left = attributeValue(a, key)
        const right = attributeValue(b, key)
        result =
          typeof left === 'number' && typeof right === 'number'
            ? left - right
            : String(left).localeCompare(String(right), 'de')
      }

      // Stable tie-break so rows never shuffle between renders.
      if (result === 0) {
        return (a.externalNumber ?? '').localeCompare(b.externalNumber ?? '', 'de')
      }
      return result * direction
    })
  }, [data, state])
}
